import { PerformanceMonitor } from './performance-monitor.js';
/**
 * PluginManager handles registration, initialization, and execution of optimization plugins
 */
export class PluginManager {
    constructor(config = {}) {
        this.plugins = [];
        this.config = config;
        this.performanceMonitor = new PerformanceMonitor();
        this.initialized = false;
        // Initialize global metrics object
        window.__OPTIMIZER_METRICS__ = {
            initTime: 0,
            pluginInitTime: 0,
            executionTime: 0,
            observerCallbacks: 0,
            totalBlockingTime: 0,
            pluginTimes: {}
        };
    }
    /**
     * Register plugins in the order they should execute
     * Plugins will be executed in the exact order they are registered
     */
    registerPlugins(plugins) {
        if (this.initialized) {
            throw new Error('Cannot register plugins after initialization');
        }
        // Filter enabled plugins and store in execution order
        this.plugins = plugins.filter(p => p.enabled);
        console.log(`[PluginManager] Registered ${this.plugins.length} plugins in execution order:`, this.plugins.map(p => p.name));
    }
    /**
     * Initialize all registered plugins
     */
    async initialize() {
        if (this.initialized) {
            console.warn('[PluginManager] Already initialized');
            return [];
        }
        const startTime = performance.now();
        console.log('[PluginManager] Initializing plugins...');
        const initResults = [];
        for (const plugin of this.plugins) {
            try {
                const pluginStartTime = performance.now();
                await this.performanceMonitor.measure(`${plugin.name}.init`, async () => {
                    await plugin.init();
                });
                const pluginInitTime = performance.now() - pluginStartTime;
                plugin.metrics.initTime = pluginInitTime;
                initResults.push({
                    plugin: plugin.name,
                    success: true,
                    initTime: pluginInitTime
                });
                console.log(`[PluginManager] Initialized: ${plugin.name} (${pluginInitTime.toFixed(2)}ms)`);
            }
            catch (error) {
                console.error(`[PluginManager] Failed to initialize ${plugin.name}:`, error);
                initResults.push({
                    plugin: plugin.name,
                    success: false,
                    error: error.message
                });
                // Disable failed plugin
                plugin.enabled = false;
            }
        }
        // Update execution list to exclude disabled plugins
        this.plugins = this.plugins.filter(p => p.enabled);
        const totalInitTime = performance.now() - startTime;
        window.__OPTIMIZER_METRICS__.initTime = totalInitTime;
        window.__OPTIMIZER_METRICS__.pluginInitTime = totalInitTime;
        this.initialized = true;
        console.log(`[PluginManager] Initialization complete (${totalInitTime.toFixed(2)}ms)`);
        return initResults;
    }
    /**
     * Execute all enabled plugins in registration order
     */
    async execute() {
        if (!this.initialized) {
            throw new Error('PluginManager must be initialized before execution');
        }
        const startTime = performance.now();
        const results = [];
        for (const plugin of this.plugins) {
            try {
                const pluginStartTime = performance.now();
                await plugin.execute();
                const executionTime = performance.now() - pluginStartTime;
                results.push({
                    plugin: plugin.name,
                    success: true,
                    executionTime,
                    metrics: plugin.getMetrics()
                });
                console.log(`[PluginManager] Executed: ${plugin.name} (${executionTime.toFixed(2)}ms)`);
            }
            catch (error) {
                console.error(`[PluginManager] Failed to execute ${plugin.name}:`, error);
                results.push({
                    plugin: plugin.name,
                    success: false,
                    error: error.message,
                    metrics: plugin.getMetrics()
                });
            }
        }
        const totalExecutionTime = performance.now() - startTime;
        window.__OPTIMIZER_METRICS__.executionTime = totalExecutionTime;
        console.log(`[PluginManager] Execution complete (${totalExecutionTime.toFixed(2)}ms)`);
        // Check if we exceeded performance budget
        this.checkPerformanceBudget();
        return results;
    }
    /**
     * Check if performance budgets were exceeded
     */
    checkPerformanceBudget() {
        const metrics = window.__OPTIMIZER_METRICS__;
        const budgets = {
            initTime: 10,
            executionTime: 50,
            totalBlockingTime: 50,
            ...this.config.performanceBudgets
        };
        const violations = [];
        if (metrics.initTime > budgets.initTime) {
            violations.push(`Init time ${metrics.initTime.toFixed(2)}ms exceeds budget of ${budgets.initTime}ms`);
        }
        if (metrics.executionTime > budgets.executionTime) {
            violations.push(`Execution time ${metrics.executionTime.toFixed(2)}ms exceeds budget of ${budgets.executionTime}ms`);
        }
        const totalTime = metrics.initTime + metrics.executionTime;
        if (totalTime > budgets.totalBlockingTime) {
            violations.push(`Total blocking time ${totalTime.toFixed(2)}ms exceeds budget of ${budgets.totalBlockingTime}ms`);
        }
        if (violations.length > 0) {
            console.warn('[PluginManager] Performance budget violations:', violations);
            // Log individual plugin times to identify bottlenecks
            const pluginTimes = Object.entries(metrics.pluginTimes)
                .sort(([, a], [, b]) => b - a)
                .map(([name, time]) => `${name}: ${time.toFixed(2)}ms`);
            console.warn('[PluginManager] Plugin execution times:', pluginTimes);
        }
    }
    /**
     * Get a specific plugin by name
     */
    getPlugin(name) {
        return this.plugins.find(p => p.name === name);
    }
    /**
     * Get all plugin metrics
     */
    getAllMetrics() {
        const pluginMetrics = {};
        for (const plugin of this.plugins) {
            pluginMetrics[plugin.name] = plugin.getMetrics();
        }
        return {
            global: window.__OPTIMIZER_METRICS__,
            plugins: pluginMetrics,
            performance: this.performanceMonitor.getReport()
        };
    }
    /**
     * Clean up all plugins and resources
     */
    async cleanup() {
        console.log('[PluginManager] Cleaning up...');
        for (const plugin of this.plugins) {
            try {
                await plugin.cleanup();
            }
            catch (error) {
                console.error(`[PluginManager] Error cleaning up ${plugin.name}:`, error);
            }
        }
        this.plugins = [];
        this.initialized = false;
    }
}
//# sourceMappingURL=plugin-manager.js.map